/**
 * XmlPreflightDialog — Pre-Flight-Check vor dem UVA/UStVA-XML-Download (Juni 2026).
 *
 * Zeigt eine Checkliste (FASTNR/Steuernummer, Zeitraum, Kennzahlen-Konsistenz,
 * Bewegungsdaten, Engine-Hinweise) BEVOR die Übermittlungsdatei erzeugt wird.
 * Download ist nur möglich, wenn kein Check fehlschlägt (status="fail").
 *
 * Wird von der FL-Voranmeldung-Page UND dem SB-Voranmeldung-Tab genutzt —
 * der API-Zugriff wird über die Props fetchPreflight/onDownload injiziert.
 */
import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { CheckCircle2, AlertTriangle, XCircle, Download, Loader2, ShieldCheck } from "lucide-react";

const STATUS_STYLES = {
  ok: "border-emerald-100 bg-emerald-50/50",
  warn: "border-amber-200 bg-amber-50/60",
  fail: "border-rose-200 bg-rose-50/60",
};

function StatusIcon({ status }) {
  if (status === "ok") return <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" />;
  if (status === "warn") return <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />;
  return <XCircle className="h-4 w-4 text-rose-600 shrink-0 mt-0.5" />;
}

export function XmlPreflightDialog({ filing, onClose, fetchPreflight, onDownload }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!filing) return;
    setData(null);
    setError(null);
    setLoading(true);
    fetchPreflight(filing)
      .then(setData)
      .catch((e) => setError(e.response?.data?.detail || "Pre-Flight-Check fehlgeschlagen."))
      .finally(() => setLoading(false));
  }, [filing?.id]);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await onDownload(filing);
      onClose();
    } finally {
      setDownloading(false);
    }
  };

  const authority = filing?.country === "AT" ? "FinanzOnline" : "ELSTER";

  return (
    <Dialog open={!!filing} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="xml-preflight-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-blue-600" />
            Pre-Flight-Check
          </DialogTitle>
          <DialogDescription>
            Prüfung vor dem {authority}-Export — {filing?.period_label}
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="flex items-center gap-2 py-6 justify-center text-sm text-slate-500" data-testid="preflight-loading">
            <Loader2 className="h-4 w-4 animate-spin" />
            Checks laufen…
          </div>
        )}
        {error && <p className="text-sm text-rose-600 py-2" data-testid="preflight-error">{error}</p>}

        {data && (
          <div className="space-y-2 max-h-80 overflow-y-auto pr-1" data-testid="preflight-checklist">
            {data.checks.map((c, i) => (
              <div
                key={`${c.id}-${i}`}
                className={`flex items-start gap-2.5 rounded-lg border px-3 py-2.5 ${STATUS_STYLES[c.status] || STATUS_STYLES.warn}`}
                data-testid={`preflight-check-${c.id}-${i}`}
              >
                <StatusIcon status={c.status} />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-800 leading-snug">{c.label}</p>
                  {c.detail && <p className="text-xs text-slate-500 mt-0.5 leading-snug">{c.detail}</p>}
                </div>
              </div>
            ))}
          </div>
        )}

        {data && !data.can_download && (
          <p className="text-xs text-rose-600" data-testid="preflight-blocked-note">
            Der Export ist blockiert, bis die rot markierten Punkte behoben sind — sonst würde
            {filing?.country === "AT" ? " FinanzOnline" : " ELSTER"} die Datei ablehnen.
          </p>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="preflight-cancel-btn">
            Abbrechen
          </Button>
          <Button
            onClick={handleDownload}
            disabled={!data?.can_download || downloading}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="preflight-download-btn"
          >
            {downloading
              ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
              : <Download className="h-4 w-4 mr-1.5" />}
            XML herunterladen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default XmlPreflightDialog;
